import { invokeFunction, ApiError } from "@/lib/api";
import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import { defaultFleetAsset, type FleetAssetRecord, type FleetAssetStatus, type FleetListMeta } from "./types";

export type { FleetAssetRecord, FleetAssetStatus, FleetListMeta, ServiceUrgency } from "./types";

interface FleetRow {
  id: string;
  unit: string;
  asset_type: string;
  status: FleetAssetStatus;
  next_service_due: string | null;
  fleetio_id: string | null;
  notes: string | null;
  updated_at: string;
}

const LS_KEY = "bb_fleet_assets_v2";

const seedFleet: FleetAssetRecord[] = [
  defaultFleetAsset({
    id: "fleet-trk-101",
    unit: "TRK-101",
    fleetGroup: "Company Driver",
    vehicleType: "Semi-Trailer Truck",
    equipment: "2022 Freightliner Cascadia",
    assetType: "Semi-Trailer Truck · 2022 Freightliner Cascadia",
    assignedDriver: "Preview Driver",
    odometer: "275,517 mi",
    ownership: "owned",
    status: "active",
    serviceUrgency: "ok",
    nextServiceDue: "2026-07-10",
    nextServiceTask: "PM inspection",
  }),
  defaultFleetAsset({
    id: "fleet-trk-104",
    unit: "TRK-104",
    fleetGroup: "Company Driver",
    vehicleType: "Semi-Trailer Truck",
    equipment: "2020 Peterbilt 579",
    assetType: "Semi-Trailer Truck · 2020 Peterbilt 579",
    assignedDriver: "J. Smith",
    odometer: "412,800 mi",
    ownership: "owned",
    status: "maintenance",
    serviceUrgency: "overdue",
    nextServiceDue: "2026-06-09",
    nextServiceTask: "DOT inspection",
    openWorkOrders: 1,
    notes: "PM service in shop.",
  }),
  defaultFleetAsset({
    id: "fleet-trl-220",
    unit: "TRL-220",
    fleetGroup: "Tanker",
    vehicleType: "Tanker",
    equipment: "2022 Polar Tank",
    assetType: "Tanker · 2022 Polar Tank",
    status: "active",
    serviceUrgency: "due_soon",
    nextServiceDue: "2026-08-01",
    nextServiceTask: "Tank wash",
  }),
  defaultFleetAsset({
    id: "fleet-trl-318",
    unit: "TRL-318",
    fleetGroup: "Tanker",
    vehicleType: "Tanker",
    equipment: "2021 Brenner Tank",
    assetType: "Tanker · 2021 Brenner Tank",
    fleetioStatus: "Inactive",
    status: "out_of_service",
    serviceUrgency: "none",
    nextServiceDue: "2026-06-20",
    openIssues: 1,
    notes: "Waiting on valve repair.",
  }),
];

const supabaseReady = () => isSupabaseConfigured && !!supabase;

function rowToAsset(row: FleetRow): FleetAssetRecord {
  return defaultFleetAsset({
    id: row.id,
    unit: row.unit,
    vehicleType: row.asset_type,
    equipment: row.asset_type,
    assetType: row.asset_type,
    status: row.status,
    nextServiceDue: row.next_service_due ?? "",
    fleetioId: row.fleetio_id ?? "",
    notes: row.notes ?? "",
    updatedAt: row.updated_at,
  });
}

function enrichPreviewAsset(row: FleetAssetRecord): FleetAssetRecord {
  const seed = seedFleet.find((item) => item.unit === row.unit);
  if (!seed) return defaultFleetAsset(row);
  return defaultFleetAsset({
    ...seed,
    ...row,
    notes: row.notes || seed.notes,
    status: row.status ?? seed.status,
  });
}

function localReadAll(): FleetAssetRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as FleetAssetRecord[];
      return parsed.map(enrichPreviewAsset);
    }
  } catch {
    /* ignore malformed cache */
  }
  localStorage.setItem(LS_KEY, JSON.stringify(seedFleet));
  return [...seedFleet];
}

function localWriteAll(assets: FleetAssetRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(assets));
}

const byUnit = (a: FleetAssetRecord, b: FleetAssetRecord) => a.unit.localeCompare(b.unit);

async function listFleetFromEdge(): Promise<FleetAssetRecord[]> {
  const { assets } = await invokeFunction<{ assets: FleetAssetRecord[]; meta: FleetListMeta }>(
    "fleet",
    { action: "list" },
  );
  return assets;
}

async function listFleetFromDbOverlay(): Promise<FleetAssetRecord[]> {
  const { data, error } = await supabase!
    .from("fleet_assets")
    .select("*")
    .order("unit", { ascending: true });
  if (error) throw error;
  return (data as FleetRow[]).map(rowToAsset);
}

export async function listFleetAssets(): Promise<FleetAssetRecord[]> {
  if (supabaseReady()) {
    try {
      return await listFleetFromEdge();
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        const assets = await listFleetFromDbOverlay();
        return assets.length > 0 ? assets : [];
      }
      throw err;
    }
  }
  return localReadAll().sort(byUnit);
}

export function assetNeedsAttention(asset: FleetAssetRecord): boolean {
  // Service reminders are the actionable maintenance signal. Fleetio's
  // issues/work-order counts are lifetime totals, so they are shown as detail
  // only and intentionally excluded here (mirrors deriveOperationalStatus).
  return (
    asset.status !== "active" ||
    asset.serviceUrgency === "overdue" ||
    asset.serviceUrgency === "due_soon"
  );
}

export async function countFleetNeedingAttention(): Promise<number> {
  const assets = await listFleetAssets();
  return assets.filter(assetNeedsAttention).length;
}

export async function updateFleetAsset(
  asset: Pick<FleetAssetRecord, "id" | "fleetioId" | "unit" | "assetType">,
  changes: { status?: FleetAssetStatus; notes?: string },
): Promise<void> {
  if (supabaseReady()) {
    try {
      await invokeFunction("fleet", {
        action: "update",
        id: asset.id,
        fleetioId: asset.fleetioId,
        unit: asset.unit,
        assetType: asset.assetType,
        ...(changes.status && !asset.fleetioId ? { status: changes.status } : {}),
        ...(changes.notes !== undefined ? { notes: changes.notes } : {}),
      });
      return;
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 503)) throw err;
    }

    const { error } = await supabase!
      .from("fleet_assets")
      .update({
        ...(changes.status && !asset.fleetioId ? { status: changes.status } : {}),
        ...(changes.notes !== undefined ? { notes: changes.notes } : {}),
      })
      .eq("id", asset.id);
    if (error) throw error;
    return;
  }

  localWriteAll(
    localReadAll().map((row) =>
      row.id === asset.id
        ? {
            ...row,
            status: changes.status ?? row.status,
            notes: changes.notes ?? row.notes,
            updatedAt: new Date().toISOString(),
          }
        : row,
    ),
  );
}
